export type OrderFilter = 'ALL' | 'PENDING_REDEEM' | 'REDEEMED' | 'REFUND';

export interface NormalizedAdminOrder {
 id: string;
 sourceId: string | null;
 buyer: string;
 item: string;
 amount: number;
 status: string;
 rawStatus: string;
 time: string;
 activeVoucherCount: number;
 voucherCount: number;
 quantity: number;
 totalAmountFen: number;
 createdAt: string | null;
 paidAt: string | null;
 packageSnapshot?: Record<string, unknown>;
}

export interface OrderFilterInput {
  rawStatus?: string;
  activeVoucherCount?: number;
}

function asRecord(value: unknown): Record<string, unknown> {
 return value && typeof value === 'object' ? value as Record<string, unknown> : {};
}

function firstNonEmptyString(...values: unknown[]): string | null {
 for (const value of values) {
  if (typeof value === 'string' && value.trim()) return value.trim();
 }
 return null;
}

function finiteNonNegativeNumber(value: unknown, fallback: number): number {
 const number = typeof value === 'number' ? value : typeof value === 'string' && value.trim() ? Number(value) : Number.NaN;
 return Number.isFinite(number) && number >= 0 ? number : fallback;
}

function positiveInteger(value: unknown, fallback: number): number {
 const number = finiteNonNegativeNumber(value, fallback);
 return number > 0 ? Math.max(1, Math.floor(number)) : fallback;
}

export function formatAdminDateTime(value: unknown): string {
 const date = value instanceof Date ? value : new Date(typeof value === 'number' ? value : String(value ?? ''));
 if (!Number.isFinite(date.getTime())) return '—';
 const parts = new Intl.DateTimeFormat('zh-CN', {
  timeZone: 'Asia/Shanghai',
  year: 'numeric',
  month: 'numeric',
  day: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
 }).formatToParts(date);
 const get = (type: Intl.DateTimeFormatPartTypes) => parts.find((part) => part.type === type)?.value;
 const year = get('year');
 const month = get('month');
 const day = get('day');
 const hour = get('hour');
 const minute = get('minute');
 return year && month && day && hour && minute ? `${year}年${month}月${day}日 ${hour}:${minute}` : '—';
}

export function normalizeAdminOrder(input: unknown, index: number): NormalizedAdminOrder {
 const source = asRecord(input);
 const packageSnapshot = asRecord(source.packageSnapshot ?? source.package_snapshot);
 const sourceId = firstNonEmptyString(source.id, source.orderId, source.order_id);
 const quantity = positiveInteger(source.quantity, 1);
 const totalAmountFen = finiteNonNegativeNumber(source.totalAmountFen ?? source.total_amount_fen, 0);
 const rawStatus = firstNonEmptyString(source.status, source.orderStatus, source.order_status) ?? 'UNKNOWN';
 const buyerId = firstNonEmptyString(source.buyerOpenId, source.buyerId, source.buyer_open_id, source.buyer_id);
 const packageName = firstNonEmptyString(packageSnapshot.name, source.packageName, source.package_name);
 const createdAt = firstNonEmptyString(source.createdAt, source.created_at);
 const paidAt = firstNonEmptyString(source.paidAt, source.paid_at);
 const activeVoucherCount = source.activeVoucherCount === undefined
  ? (['PAID', 'PARTIALLY_REDEEMED', 'PARTIALLY_REFUNDED'].includes(rawStatus) ? 1 : 0)
  : positiveInteger(source.activeVoucherCount, 0);
 const voucherCount = positiveInteger(source.voucherCount, 0);
 const buyer = buyerId ? `微信用户 · ${buyerId.slice(-4)}` : '微信用户';
 const item = `${packageName ?? '团购套餐'} × ${quantity}`;

 return {
  id: sourceId ?? `订单-${Math.max(1, index + 1)}`,
  sourceId,
  buyer,
  item,
  amount: totalAmountFen / 100,
  status: getOrderStatusLabel(rawStatus),
  rawStatus,
  time: formatAdminDateTime(createdAt),
  activeVoucherCount,
  voucherCount,
  quantity,
  totalAmountFen,
  createdAt,
  paidAt,
  ...(Object.keys(packageSnapshot).length > 0 ? { packageSnapshot } : {}),
 };
}

export function orderMatchesFilter(order: OrderFilterInput, filter: OrderFilter): boolean {
 const status = order.rawStatus ?? '';
 if (filter === 'ALL') return status !== 'PENDING_PAYMENT';
 if (filter === 'REDEEMED') return status === 'FULLY_REDEEMED';
 if (filter === 'REFUND') return status === 'PARTIALLY_REFUNDED' || status === 'REFUNDED';
 return ['PAID', 'PARTIALLY_REDEEMED'].includes(status) && (order.activeVoucherCount ?? 0) > 0;
}

export function getOrderStatusLabel(status: string | undefined): string {
  switch (status) {
    case 'PENDING_PAYMENT': return '待支付';
    case 'PAID': return '待核销';
    case 'PARTIALLY_REDEEMED': return '部分核销';
    case 'FULLY_REDEEMED': return '已核销';
    case 'PARTIALLY_REFUNDED': return '部分退款';
    case 'REFUNDED': return '已退款';
    default: return '状态待同步';
  }
}

export function packageStatusLabel(status: string | undefined, archivedAt: string | null | undefined): string {
  if (archivedAt) return '已归档';
  if (status === 'PUBLISHED') return '已上架';
  if (status === 'OFF_SHELF') return '已下架';
  return '草稿';
}
